import { Bell, BellOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ensureOrderPushSubscription,
  hasOptedIntoOrderPush,
  isOrderPushSupported,
  type OrderPushFailure,
  removeOrderPushSubscription,
} from "@/lib/pwa/register-customer-sw";

const FAILURE_COPY: Record<OrderPushFailure, { fallback: string; key: string }> = {
  denied: {
    fallback: "Tenés que permitir las notificaciones en tu navegador.",
    key: "tracking.notifyDenied",
  },
  failed: {
    fallback: "No pudimos activar los avisos. Probá de nuevo.",
    key: "tracking.notifyFailed",
  },
  unconfigured: {
    fallback: "Los avisos no están disponibles en este momento.",
    key: "tracking.notifyUnavailable",
  },
  unsupported: {
    fallback: "Tu navegador no admite avisos para este pedido.",
    key: "tracking.notifyUnsupported",
  },
};

/**
 * Opt into notifications about ONE Order (issue #93).
 *
 * Shown to guests as well as signed-in Consumers: a guest is the customer with
 * no other way to hear anything, since the notification is tied to the Order
 * rather than to an account. The choice outlives the tab — the subscription is a
 * row on the server, not page state.
 */
export function OrderNotificationsToggle({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setSupported(isOrderPushSupported());
    setEnabled(hasOptedIntoOrderPush(orderId));
  }, [orderId]);

  if (!supported) {
    return null;
  }

  async function handleEnable() {
    setPending(true);
    const result = await ensureOrderPushSubscription(orderId);
    setPending(false);

    if (result.ok) {
      setEnabled(true);
      toast.success(t("tracking.notifyEnabled", "Te vamos a avisar sobre este pedido."));
      return;
    }

    const copy = FAILURE_COPY[result.reason];
    toast.error(t(copy.key, copy.fallback));
  }

  async function handleDisable() {
    setPending(true);
    await removeOrderPushSubscription(orderId);
    setPending(false);
    setEnabled(false);
    toast.success(t("tracking.notifyDisabled", "Dejamos de avisarte sobre este pedido."));
  }

  return (
    <Button
      disabled={pending}
      onClick={enabled ? handleDisable : handleEnable}
      size="sm"
      type="button"
      variant="outline"
    >
      {enabled ? <BellOff className="mr-1 h-4 w-4" /> : <Bell className="mr-1 h-4 w-4" />}
      {enabled
        ? t("tracking.notifyStop", "No avisarme")
        : t("tracking.notifyMe", "Avisarme del estado")}
    </Button>
  );
}
